import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import {
    isUndef,
} from '@/lib/utils';
import {Toast} from 'vant';
import { string } from 'mathjs';

export interface RankState {
    /**
     * 当前正在请求
     */
    requesting: boolean;
    /**
     * 包含team数据
     *
     * id => team
     */
    teamMap: {
        [teamId: string]: TeamListItem | undefined,
    };
    /**
     * 战队完赛率排行
     * 以activityId和levelId为key
     */
    teamRank: {
        [activityId_levelId: string]: ListWrap<TeamFinishedRateItem> & ListRequesting & ListUser<TeamFinishedRateItem> | undefined,
    };
    /**
     * 战队距离排行
     * 以activityId和levelId为key
     */
    teamDistance: {
        [activityId_levelId: string]: ListWrap<TeamFinishedRateItem> & ListRequesting & ListUser<TeamFinishedRateItem> | undefined,
    };
    /**
     * 默认选择的班级id
     */
    defaultSelectedTeamId: string;
    /**
     * 个人完赛率排行
     */
    userFinish: {
        [teamId: string]: ListWrap<UserFinishedRateItem> & ListRequesting & ListUser<UserFinishedRateItem | null> | undefined,
    };
    /**
     * 今日打卡排行榜
     */
    todayDistance: {
        [teamId: string]: ListWrap<TodayDistanceItem> & ListRequesting & ListUser<TodayDistanceItem | null> | undefined,
    };
    /**
     * 个人距离排行
     */
    userDistance: {
        [teamId: string]: ListWrap<UserDistanceItem> & ListRequesting & ListUser<UserDistanceItem | null> | undefined;
    };
    /**
     * 用户的点赞情况，使用activityId_userId 为 key
     */
    thumbUpMap: {
        [activityId_userId: string]: {
            userId: string,
            count: number,
            thumbUp: boolean,
        },
    };
    /** 战队点赞榜 */
    teamThumbUp: {
        [activityId: string]: TeamThumbUp & ListRequesting | undefined,
    };
    /** 个人点赞榜 */
    userThumbUp: {
        [activity: string]: UserThumbUp & ListRequesting | undefined,
    };
    /** 邀请榜 */
    invite: {
        [activityId: string]: ListWrap<Rank.InviteItem> & ListUser<Rank.InviteItem | null> & ListRequesting | undefined,
    };
    prize: {
        [activityId: string]: ListWrap<Rank.InviteItem & { count: number; has_exited: boolean; created_at: string; status: 'pending' | 'sending' | 'sent' | 'failed' | 'canceled' }> & ListRequesting | undefined,
    };
    /** 所有人的距离排行榜 */
    userTotalDistanceRank: UserTotalDistanceItem;
    /**
     * 个人坚持排行榜
     */
    totalDays: ListWrap<TotalDaysItem> & ListRequesting & ListUser<TotalDaysItem | null>;
    /**
     * 个人里程排行榜
     */
    totalDistance: ListWrap<TotalDistanceItem> & ListRequesting & ListUser<TotalDistanceItem | null>;
    /**
     * 顶部轮播图
     */
    rankBannerMap: {
        [activityId: string]: Rank.BannerItem[] | undefined,
    };
    platformTotalDaysRank: ListWrap<string> & ListRequesting & ListUser<string>;
    platformReferrerRank: ListWrap<string> & ListRequesting & ListUser<string>;
    platformTermRank: ListWrap<string> & ListRequesting & ListUser<string>;
    platformRankItem: {
        [rankId: string]: Rank.PlatformRankItem;
    };
}
const m: Module<RankState, RootState> = {
    namespaced: true,
    state: () => ({
        requesting: false,
        teamMap: {},
        teamRank: {},
        teamDistance: {},
        defaultSelectedTeamId: '0',
        userFinish: {},
        todayDistance: {},
        userDistance: {},
        thumbUpMap: {},
        teamThumbUp: {},
        // 个人点赞榜
        userThumbUp: {},
        invite: {},
        prize: {},
        /** 所有人的距离排行榜 */
        userTotalDistanceRank: {
            current: null,
            list: [],
        },
        /** 个人坚持排行榜 */
        totalDays: {
            user: null,
            list: [],
            requesting: false,
            loaded: false,
        },
        /** 个人里程排行榜 */
        totalDistance: {
            user: null,
            list: [],
            requesting: false,
            loaded: false,
        },
        rankBannerMap: {},
        platformTotalDaysRank: {
            list: [],
            loaded: false,
            requesting: false,
            user: null,
        },
        platformReferrerRank: {
            list: [],
            loaded: false,
            requesting: false,
            user: null,
        },
        platformTermRank: {
            list: [],
            loaded: false,
            requesting: false,
            user: null,
        },
        platformRankItem: {},
    }),
    mutations: {
        setRequesting(state, requesting) {
            state.requesting = requesting;
        },
        setUserFinishRequesting(state, { teamId, requesting }) {
            Vue.set(state.userFinish[teamId]!, 'requesting', requesting);
        },
        setUserDistanceRequesting(state, { teamId, requesting }) {
            Vue.set(state.userDistance[teamId]!, 'requesting', requesting);
        },
        setTodayDistanceRequesting(state, { teamId, requesting }) {
            Vue.set(state.todayDistance[teamId]!, 'requesting', requesting);
        },
        /** 初始化 */
        initTeamRank(state, key) {
            Vue.set(state.teamRank, key, {
                list: [],
                user: null,
                loaded: false,
                requesting: false,
            });
        },
        /** 战队完赛率请求中 */
        setTeamRankReq(state, { key, bol }) {
            state.teamRank[key]!.requesting = bol;
        },
        /**
         * 记录战队完赛率数据
         * @param {} list
         */
        concatTeamRank(state, { key, list }) {
            state.teamRank[key]!.list = state.teamRank[key]!.list.concat(list);
        },
        /**
         * 记录战队完赛率个人数据
         * @param {} user 用户数据
         */
        setTeamRankUser(state, { key, user }) {
            state.teamRank[key]!.user = user;
        },
        /**
         * 记录战队完赛率是否加载
         */
        setTeamRankLoaded(state, { key, loaded }) {
            state.teamRank[key]!.loaded = loaded;
        },
        /** 初始化 */
        initTeamDistance(state, key) {
            Vue.set(state.teamDistance, key, {
                list: [],
                user: null,
                loaded: false,
                requesting: false,
            });
        },
        /** 战队完赛率请求中 */
        setTeamDistanceReq(state, { key, bol }) {
            state.teamDistance[key]!.requesting = bol;
        },
        /**
         * 记录战队完赛率数据
         * @param {} list
         */
        concatTeamDistance(state, { key, list }) {
            state.teamDistance[key]!.list = state.teamDistance[key]!.list.concat(list);
        },
        /**
         * 记录战队完赛率个人数据
         * @param {} user 用户数据
         */
        setTeamDistanceUser(state, { key, user }) {
            state.teamDistance[key]!.user = user;
        },
        /**
         * 记录战队完赛率是否加载
         */
        setTeamDistanceLoaded(state, { key, loaded }) {
            state.teamDistance[key]!.loaded = loaded;
        },
        /**
         * 个人完赛排行
         *
         * @param {} teamId 班级id
         * @param {} data 排行数据
         *  - user 用户数据
         *  - list 排行数据
         */
        setUserFinish(state, { activityId, teamId, data, loaded }: { activityId: string, teamId: string, data: { current: UserFinishedRateItem | null, list: UserFinishedRateItem[] }, loaded: boolean }) {
            const length = state.userFinish[teamId] ? state.userFinish[teamId]!.list.length : 0;
            // 处理点赞数据
            data.list.forEach((item: UserFinishedRateItem, index: number) => {
                const key: string = activityId + '_' + item.id;
                item.id = key;
                item.rank = length + index + 1;
                Vue.set(state.thumbUpMap, key, {
                    userId: key,
                    count: item.thumb_up_count,
                    thumbUp: item.has_thumb_up,
                });
            });
            let tempList = data.list;
            let tempUser = data.current;
            if (state.userFinish[teamId]) {
                tempList = state.userFinish[teamId]!.list.concat(tempList);
                tempUser = tempUser ? tempUser : state.userFinish[teamId]!.user;
            }
            Vue.set(state.userFinish, teamId, {
                requesting: false,
                user: tempUser,
                list: tempList,
                loaded,
            });
        },
        /**
         * 记录用户距离
         */
        setUserDistance(state, { activityId, teamId, data, loaded }: { activityId: string, teamId: string, data: {
            current: UserDistanceItem | null,
            list: UserDistanceItem[],
        }, loaded: boolean }) {
            const length = state.userDistance[teamId] ? state.userDistance[teamId]!.list.length : 0;
            data.list.forEach((item: UserDistanceItem, index: number) => {
                const key: string = activityId + '_' + item.id;
                item.id = key;
                item.rank = length + index + 1;
                Vue.set(state.thumbUpMap, key, {
                    userId: key,
                    count: item.thumb_up_count,
                    thumbUp: item.has_thumb_up,
                });
            });
            let tempList = data.list;
            let tempUser = data.current;
            if (state.userDistance[teamId]) {
                tempList = state.userDistance[teamId]!.list.concat(tempList);
                tempUser = tempUser ? tempUser : state.userDistance[teamId]!.user;
            }
            Vue.set(state.userDistance, teamId, {
                requesting: false,
                user: tempUser,
                list: tempList,
                loaded,
            });
        },
        /**
         * 今日打卡
         */
        setTodayDistance(state, { activityId, teamId, data, loaded }: { activityId: string, teamId: string, data: {
            current: TodayDistanceItem | null,
            list: TodayDistanceItem[],
        }, loaded: boolean }) {
            const length = state.todayDistance[teamId] ? state.todayDistance[teamId]!.list.length : 0;
            data.list.forEach((item: TodayDistanceItem, index: number) => {
                const key: string = activityId + '_' + item.id;
                item.id = key;
                item.rank = length + index + 1;
                Vue.set(state.thumbUpMap, key, {
                    userId: key,
                    count: item.thumb_up_count,
                    thumbUp: item.has_thumb_up,
                });
            });
            let tempList = data.list;
            let tempUser = data.current;
            if (state.todayDistance[teamId]) {
                tempList = state.todayDistance[teamId]!.list.concat(tempList);
                tempUser = tempUser ? tempUser : state.todayDistance[teamId]!.user;
            }
            Vue.set(state.todayDistance, teamId, {
                requesting: false,
                user: tempUser,
                list: tempList,
                loaded,
            });
        },
        /**
         * 记录点赞
         *
         * @param {} userId 用户id
         * @param {} count 点赞数量
         * @param {} thumbUp 是否点赞
         */
        updateThumbUp(state, { userId, count, thumbUp }) {
            Vue.set(state.thumbUpMap, userId, {
                userId,
                count,
                thumbUp,
            });
        },
        /** 初始化战队点赞数据 */
        initTeamThumbUp(state, activityId) {
            Vue.set(state.teamThumbUp, activityId, {
                list: [],
                user: null,
                loaded: false,
                requesting: false,
            });
        },
        /** 战队点赞数据请求中 */
        setTeamThumbUpReq(state, { activityId, bol }) {
            state.teamThumbUp[activityId]!.requesting = bol;
        },
        /**
         * 记录战队点赞数据
         * @param {} list
         */
        concatTeamThumbUp(state, { activityId, list }) {
            state.teamThumbUp[activityId]!.list = state.teamThumbUp[activityId]!.list.concat(list);
        },
        /**
         * 记录战队点赞个人数据
         * @param {} user 战队数据
         */
        setTeamThumbUpUser(state, { activityId, user }) {
            state.teamThumbUp[activityId]!.user = user;
        },
        /**
         * 记录用户点赞是否加载
         */
        setTeamThumbUpLoaded(state, { activityId, loaded }) {
            state.teamThumbUp[activityId]!.loaded = loaded;
        },
        /** 初始化用户点赞数据 */
        initUserThumbUp(state, activityId) {
            Vue.set(state.userThumbUp, activityId, {
                list: [],
                user: null,
                loaded: false,
                requesting: false,
            });
        },
        /** 用户点赞请求中 */
        setUserThumbUpReq(state, { activityId, bol }) {
            state.userThumbUp[activityId]!.requesting = bol;
        },
        /**
         * 记录用户点赞数据
         * @param {} list
         */
        concatUserThumbUp(state, { activityId, list }) {
            const dealList = list.filter((item: UserThumbUpItem) => {
                return item.thumb_up_count !== 0;
            });
            state.userThumbUp[activityId]!.list = state.userThumbUp[activityId]!.list.concat(dealList);
        },
        /**
         * 记录用户点赞个人数据
         * @param {} user 用户数据
         */
        setUserThumbUpUser(state, { activityId, user }) {
            state.userThumbUp[activityId]!.user = user;
        },
        /**
         * 记录用户点赞是否加载
         */
        setUserThumbUpLoaded(state, { activityId, loaded }) {
            state.userThumbUp[activityId]!.loaded = loaded;
        },

        /** 邀请人数排行是否加载完成 */
        setInvitePrizeLoaded(state, { activityId, loaded }) {
            state.prize[activityId]!.loaded = loaded;
        },
        /** 邀请人数排行是否处于加载中 */
        setInvitePrizeReq(state, { activityId, req }) {
            state.prize[activityId]!.requesting = req;
        },
        /** 邀请人数排行 */
        concatInvitePrize(state, { activityId, list }) {
            state.prize[activityId]!.list = state.prize[activityId]!.list.concat(list);
        },
        /** 初始化邀请人数排行 */
        initInvitePrize(state, activityId) {
            Vue.set(state.prize, activityId, {
                list: [],
                user: null,
                loaded: false,
                requesting: false,
            });
        },

        /** 邀请人数排行是否加载完成 */
        setInviteLoaded(state, { activityId, loaded }) {
            state.invite[activityId]!.loaded = loaded;
        },
        /** 邀请人数排行是否处于加载中 */
        setInviteReq(state, { activityId, req }) {
            state.invite[activityId]!.requesting = req;
        },
        /** 邀请人数排行 */
        concatInvite(state, { activityId, list }) {
            state.invite[activityId]!.list = state.invite[activityId]!.list.concat(list);
        },
        /** 初始化邀请人数排行 */
        initInvite(state, activityId) {
            Vue.set(state.invite, activityId, {
                list: [],
                user: null,
                loaded: false,
                requesting: false,
            });
        },
        /** 记录邀请人数个人数据 */
        setInviteCurrent(state, { activityId, current }) {
            state.invite[activityId]!.user = current;
        },
        /**
         * 记录所有人的距离排行榜
         */
        setUserTotalDistanceRank(state, data) {
            state.userTotalDistanceRank = data;
            data.list.forEach((item: UserDistanceItem) => {
                Vue.set(state.thumbUpMap, String(item.id), {
                    userId: item.id,
                    count: item.thumb_up_count,
                    thumbUp: item.has_thumb_up,
                });
            });
        },
        /** 个人坚持排行榜 */
        setTotalDaysRequesting(state, requesting) {
            state.totalDays.requesting = requesting;
        },
        concatTotalDaysList(state, data) {
            state.totalDays.list = state.totalDays.list.concat(data.list);
            state.totalDays.user = data.current;
        },
        setTotalDaysLoaded(state, loaded) {
            state.totalDays.loaded = loaded;
        },
        setRankBannerMap(state, { activityId, list }: { activityId: string, list: Rank.BannerItem[] }) {
            Vue.set(state.rankBannerMap, activityId, list);
        },
        /** 个人里程排行榜 */
        setTotalDistanceRequesting(state, requesting) {
            state.totalDistance.requesting = requesting;
        },
        concatTotalDistanceList(state, data) {
            state.totalDistance.list = state.totalDistance.list.concat(data.list);
            state.totalDistance.user = data.current;
        },
        setTotalDistanceLoaded(state, loaded) {
            state.totalDistance.loaded = loaded;
        },
        /**
         * 平台坚持天数排行榜
         */
        concatPlatformTotalDaysRank(state, list) {
            const ids: string[] = [];
            for (const item of list) {
                const id = 'total_days_' + item.id;
                ids.push(id);
                if (!state.platformRankItem[id]) {
                    Vue.set(state.platformRankItem, id, item);
                }
            }
            state.platformTotalDaysRank.list.push(...ids);
        },
        setPlatformTotalDaysRankLoaded(state, loaded) {
            state.platformTotalDaysRank.loaded = loaded;
        },
        setPlatformTotalDaysRankReq(state, req) {
            state.platformTotalDaysRank.requesting = req;
        },
        setPlatformTotalDaysRankUser(state, user) {
            const id = 'total_days_' + user.id;
            Vue.set(state.platformRankItem, id, user);
            state.platformTotalDaysRank.user = id;
        },
        /**
         * 平台影响排行榜
         */
        concatPlatformReferrerRank(state, list) {
            const ids: string[] = [];
            for (const item of list) {
                const id = 'referrer_' + item.id;
                ids.push(id);
                if (!state.platformRankItem[id]) {
                    Vue.set(state.platformRankItem, id, item);
                }
            }
            state.platformReferrerRank.list.push(...ids);
        },
        setPlatformReferrerRankLoaded(state, loaded) {
            state.platformReferrerRank.loaded = loaded;
        },
        setPlatformReferrerRankReq(state, req) {
            state.platformReferrerRank.requesting = req;
        },
        setPlatformReferrerRankUser(state, user) {
            const id = 'referrer_' + user.id;
            Vue.set(state.platformRankItem, id, user);
            state.platformReferrerRank.user = id;
        },
        /**
         * 平台届数榜
         */
        concatPlatformTermRank(state, list) {
            const ids: string[] = [];
            for (const item of list) {
                const id = 'term_' + item.id;
                ids.push(id);
                if (!state.platformRankItem[id]) {
                    Vue.set(state.platformRankItem, id, item);
                }
            }
            state.platformTermRank.list.push(...ids);
        },
        setPlatformTermRankLoaded(state, loaded) {
            state.platformTermRank.loaded = loaded;
        },
        setPlatformTermRankReq(state, req) {
            state.platformTermRank.requesting = req;
        },
        setPlatformTermRankUser(state, user) {
            const id = 'term_' + user.id;
            Vue.set(state.platformRankItem, id, user);
            state.platformTermRank.user = id;
        },
        /**
         * 点赞
         */
        setPlatformThumbUp(state, { id, hasThumbUp }) {
            const item = state.platformRankItem[id];
            if (item) {
                item.has_thumb_up = hasThumbUp;
                item.thumb_up_count += (hasThumbUp ? 1 : -1);
            }
        },
    },
    actions: {
        /**
         * 获取个人完赛率榜单
         */
        async getUserFinishRank({ rootState, state, commit }, { activityId, teamId,  offset, count, forceUpdate = false}: { activityId: string, teamId: string,  offset: number, count: number, forceUpdate: boolean }) {
            if (!state.userFinish[teamId]) {
                Vue.set(state.userFinish, teamId, {
                    list: [],
                    user: null,
                    loaded: false,
                    requesting: false,
                });
            }
            const temp = state.userFinish[teamId];
            if (temp!.requesting || (temp!.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                temp!.loaded = false;
                temp!.list = [];
            }
            const list = temp!.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setUserFinishRequesting', {
                teamId,
                requesting: true,
            });
            const response = await Vue.axios.get('/api/v1/activity/user_finish_rate_rank', {
                params: {
                    id: activityId,
                    team_id: teamId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('setUserFinish', {
                    activityId,
                    teamId,
                    data,
                    loaded: data.list.length < count,
                });
                if (!isUndef(data.current)) {
                    const userId = rootState.user.basicInfo ? rootState.user.basicInfo.id : '';
                    const key: string = activityId + '_' + userId;
                    Vue.set(state.thumbUpMap, key, {
                        userId: key,
                        count: data.current.thumb_up_count,
                        thumbUp: data.current.has_thumb_up,
                    });
                }
            }
            commit('setUserFinishRequesting', {
                teamId,
                requesting: false,
            });
            return rdata;
        },
        /**
         * 获取个人距离排行榜
         */
        async getUserDistance({ rootState, state, commit }, { activityId, teamId, offset, count, forceUpdate = false}: { activityId: string, teamId: string, offset: number, count: number, forceUpdate: boolean }) {
            if (!state.userDistance[teamId]) {
                Vue.set(state.userDistance, teamId, {
                    requesting: false,
                    loaded: false,
                    list: [],
                    user: null,
                });
            }
            const temp = state.userDistance[teamId];
            if (temp!.requesting || (temp!.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                temp!.loaded = false;
                temp!.list = [];
            }
            const list = temp!.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setUserDistanceRequesting', {
                teamId,
                requesting: true,
            });
            const response = await Vue.axios.get('/api/v1/activity/user_team_total_distance_rank', {
                params: {
                    id: activityId,
                    team_id: teamId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('setUserDistance', {
                    activityId,
                    teamId,
                    data,
                    loaded: data.list.length < count,
                });
                if (!isUndef(data.current)) {
                    const userId = rootState.user.basicInfo ? rootState.user.basicInfo.id : '';
                    const key: string = activityId + '_' + userId;
                    Vue.set(state.thumbUpMap, key, {
                        userId: key,
                        count: data.current.thumb_up_count,
                        thumbUp: data.current.has_thumb_up,
                    });
                }
            }
            commit('setUserDistanceRequesting', {
                teamId,
                requesting: false,
            });
            return rdata;
        },
        /**
         * 今日距离排行榜
         */
        async getTodayDistance({ rootState, state, commit }, { activityId, teamId, offset, count, forceUpdate = false}: { activityId: string, teamId: string, offset: number, count: number, forceUpdate: boolean }) {
            if (!state.todayDistance[teamId]) {
                Vue.set(state.todayDistance, teamId, {
                    requesting: false,
                    loaded: false,
                    list: [],
                    user: null,
                });
            }
            const temp = state.todayDistance[teamId];
            if (temp!.requesting || (temp!.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                temp!.loaded = false;
                temp!.list = [];
            }
            const list = temp!.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setTodayDistanceRequesting', {
                teamId,
                requesting: true,
            });
            const response = await Vue.axios.get('/api/v1/activity/today_distance_rank', {
                params: {
                    id: activityId,
                    team_id: teamId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('setTodayDistance', {
                    activityId,
                    teamId,
                    data,
                    loaded: data.list.length < count,
                });
                if (!isUndef(data.current)) {
                    const userId = rootState.user.basicInfo ? rootState.user.basicInfo.id : '';
                    const key: string = activityId + '_' + userId;
                    Vue.set(state.thumbUpMap, key, {
                        userId: key,
                        count: data.current.thumb_up_count,
                        thumbUp: data.current.has_thumb_up,
                    });
                }
            }
            commit('setTodayDistanceRequesting', {
                teamId,
                requesting: false,
            });
            return rdata;
        },
        /**
         * 获取战队完赛排行榜单
         */
        async getTeamRank({ state, commit }, { activityId, levelId, offset, count, forceUpdate = false }: { activityId: Activity.Id, levelId: string, forceUpdate: boolean, offset: number, count: number }) {
            const key: string = activityId + '_' + levelId;
            if (!state.teamRank[key]) {
                commit('initTeamRank', key);
            }
            const temp = state.teamRank[key]!;
            if (temp.requesting || (temp.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                temp.loaded = false;
                temp.list = [];
            }
            const list = temp.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setTeamRankReq', {
                key,
                bol: true,
            });
            const response = await Vue.axios.get('/api/v1/activity/team_finish_rate_rank', {
                params: {
                    id: activityId,
                    level_id: levelId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatTeamRank', {
                    key,
                    list: rdata.data.list,
                });
                if (rdata.data.current) {
                    commit('setTeamRankUser', {
                        key,
                        user: rdata.data.current,
                    });
                }
                if (rdata.data.list.length < count) {
                    commit('setTeamRankLoaded', {
                        key,
                        loaded: true,
                    });
                }
            }
            commit('setTeamRankReq', {
                key,
                bol: false,
            });
            return rdata;
        },
        /**
         * 获取战队里程排行榜单
         */
        async getTeamDistance({ state, commit }, { activityId, levelId, offset, count, forceUpdate = false }: { activityId: Activity.Id, levelId: string, forceUpdate: boolean, offset: number, count: number }) {
            const key: string = activityId + '_' + levelId;
            if (!state.teamDistance[key]) {
                commit('initTeamDistance', key);
            }
            const temp = state.teamDistance[key]!;
            if (temp.requesting || (temp.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                temp.loaded = false;
                temp.list = [];
            }
            const list = temp.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setTeamDistanceReq', {
                key,
                bol: true,
            });
            const response = await Vue.axios.get('/api/v1/activity/team_level_total_distance_rank', {
                params: {
                    id: activityId,
                    level_id: levelId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatTeamDistance', {
                    key,
                    list: rdata.data.list,
                });
                if (rdata.data.current) {
                    commit('setTeamDistanceUser', {
                        key,
                        user: rdata.data.current,
                    });
                }
                if (rdata.data.list.length < count) {
                    commit('setTeamDistanceLoaded', {
                        key,
                        loaded: true,
                    });
                }
            }
            commit('setTeamDistanceReq', {
                key,
                bol: false,
            });
            return rdata;
        },
        /**
         * 战队点赞榜
         */
        async getTeamThumbUpRank({ state, commit }, { offset,
            count, activityId, forceUpdate = false }:
            { offset: number, count: number, activityId: string, forceUpdate: boolean }) {
            if (!state.teamThumbUp[activityId]) {
                commit('initTeamThumbUp', activityId);
            }
            const temp = state.teamThumbUp[activityId]!;
            if (temp.requesting || (temp.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                temp.loaded = false;
                temp.list = [];
            }
            const list = temp.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setTeamThumbUpReq', {
                activityId,
                bol: true,
            });
            const response = await Vue.axios.get('/api/v1/activity/team_thumb_up_rank', {
                params: {
                    id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatTeamThumbUp', {
                    activityId,
                    list: rdata.data.list,
                });
                if (rdata.data.current) {
                    commit('setTeamThumbUpUser', {
                        activityId,
                        user: rdata.data.current,
                    });
                }
                if (rdata.data.list.length < count) {
                    commit('setTeamThumbUpLoaded', {
                        activityId,
                        loaded: true,
                    });
                }
            }
            commit('setTeamThumbUpReq', {
                activityId,
                bol: false,
            });
            return rdata;
        },
        /**
         * 用户点赞榜
         * @param {} offset
         * @param {} count
         */
        async getUserThumbUpRank({ state, commit }, { offset,
            count, activityId, forceUpdate = false }:
            { offset: number, count: number, activityId: string, forceUpdate: boolean }) {
                if (!state.userThumbUp[activityId]) {
                    commit('initUserThumbUp', activityId);
                }
                const temp = state.userThumbUp[activityId]!;
                if (temp.requesting || (temp.loaded && !forceUpdate)) {
                    return;
                }
                if (forceUpdate) {
                    temp.loaded = false;
                    temp.list = [];
                }
                const list = temp.list;
                const end = offset + count;
                if (end <= list.length) {
                    return;
                }
                if (offset < list.length) {
                    offset = list.length;
                    count = end - offset;
                }
                commit('setUserThumbUpReq', {
                    activityId,
                    bol: true,
                });
                const response = await Vue.axios.get('/api/v1/activity/user_thumb_up_rank', {
                    params: {
                        id: activityId,
                        offset,
                        count,
                    },
                });
                const rdata = response.data;
                if (rdata.status === 0) {
                    commit('concatUserThumbUp', {
                        activityId,
                        list: rdata.data.list,
                    });
                    if (rdata.data.current) {
                        commit('setUserThumbUpUser', {
                            activityId,
                            user: rdata.data.current,
                        });
                    }
                    if (rdata.data.list.length < count) {
                        commit('setUserThumbUpLoaded', {
                            activityId,
                            loaded: true,
                        });
                    }
                }
                commit('setUserThumbUpReq', {
                    activityId,
                    bol: false,
                });
                return rdata;
        },
        /**
         * 执行点赞，同时也更新了store中的原本数据
         *
         * @param {} userId 用户id
         *
         * @return {} void
         */
        async thumbUp({ state, commit }, { activityId, userId }: { activityId: string, userId: string }) {
            const map = state.thumbUpMap;
            // 在还没有点赞的情况下才允许发送请求
            if (!map[userId]) {
                // 这里应该发生了错误
                return false;
            }
            if (map[userId].thumbUp) {
                commit('updateThumbUp', {
                    userId,
                    count: map[userId].count - 1,
                    thumbUp: false,
                });
                const rdata = await Vue.axios.post('/api/v1/participation/cancel_thumb_up', {
                    activity_id: activityId,
                    user_id: userId.split('_')[1],
                });
                return rdata;
            } else {
                // 先更新数据
                commit('updateThumbUp', {
                    userId,
                    count: map[userId].count + 1,
                    thumbUp: true,
                });
                const response = await Vue.axios.post('/api/v1/participation/create_thumb_up', {
                    activity_id: activityId,
                    user_id: userId.split('_')[1],
                });
                const rdata = response.data;
                if (rdata.status !== 0) {
                    Toast(rdata.message);
                    return;
                }
                return response;
            }
        },
        /**
         * 获取个人距离总榜 不限制战队
         * @param activityId 活动id
         */
        async getUserTotalDistanceRank({ state, commit }, activityId: string) {
            commit('setRequesting', true);
            const response = await Vue.axios.get('/api/v1/activity/user_total_distance_rank', {
                params: {
                    id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setUserTotalDistanceRank', rdata.data);
            }
            commit('setRequesting', false);
            return rdata;
        },
        /**
         * 获得邀请榜单
         */
        async getInviteRank({ state, commit }, { activityId, offset, count, forceUpdate }: { activityId
            : string, offset: number, count: number, forceUpdate?: boolean }) {
            if (!state.invite[activityId]) {
                commit('initInvite', activityId);
            }
            const invite = state.invite[activityId]!;
            if (invite.requesting || (invite.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                invite.loaded = false;
                invite.list = [];
            }
            const list = invite.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setInviteReq', { activityId, req: true });
            const response = await Vue.axios.get('/api/v1/activity/user_referrer_rank', {
                params: {
                    id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatInvite', {
                    activityId,
                    list: rdata.data.list,
                });
                commit('setInviteCurrent', {
                    activityId,
                    current: rdata.data.current,
                });
                if (rdata.data.list.length < count) {
                    commit('setInviteLoaded', {
                        activityId,
                        loaded: true,
                    });
                }
            }
            commit('setInviteReq', { activityId, req: false });
            return rdata;
        },
        /**
         * 获得邀请奖励榜单
         */
        async getInvitePrizeRank({ state, commit }, { activityId, offset, count, forceUpdate }: { activityId
            : string, offset: number, count: number, forceUpdate?: boolean }) {
            if (!state.prize[activityId]) {
                commit('initInvitePrize', activityId);
            }
            const prize = state.prize[activityId]!;
            if (prize.requesting || (prize.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                prize.loaded = false;
                prize.list = [];
            }
            const list = prize.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setInvitePrizeReq', { activityId, req: true });
            const response = await Vue.axios.get('/api/v1/invite/invite_prize', {
                params: {
                    id: activityId,
                    activity_id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatInvitePrize', {
                    activityId,
                    list: rdata.data,
                });
                if (rdata.data.length < count) {
                    commit('setInvitePrizeLoaded', {
                        activityId,
                        loaded: true,
                    });
                }
            }
            commit('setInvitePrizeReq', { activityId, req: false });
            return rdata;
        },
        /**
         * 获取个人坚持排行榜
         */
        async getTotalDaysRank({ state, commit }, { activityId, offset, count, forceUpdate = false}: { activityId: number, offset: number, count: number, forceUpdate: boolean }) {
            const temp = state.totalDays;
            if (temp!.requesting || (temp!.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                temp!.loaded = false;
                temp!.list = [];
            }
            const list = temp!.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setTotalDaysRequesting', true);
            const response = await Vue.axios.get('/api/v1/activity/user_total_days_rank', {
                params: {
                    activity_id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('concatTotalDaysList', data);
                if (rdata.data.list.length < count) {
                    commit('setTotalDaysLoaded', true);
                }
            }
            commit('setTotalDaysRequesting', false);
            return rdata;
        },

        /**
         * 获取个人里程排行榜
         */
        async getTotalDistanceRank({ state, commit }, { activityId, offset, count, forceUpdate = false}: { activityId: number, offset: number, count: number, forceUpdate: boolean }) {
            const temp = state.totalDistance;
            if (temp!.requesting || (temp!.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                temp!.loaded = false;
                temp!.list = [];
            }
            const list = temp!.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setTotalDistanceRequesting', true);
            const response = await Vue.axios.get('/api/v1/activity/user_activity_total_distance_rank', {
                params: {
                    id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('concatTotalDistanceList', data);
                if (rdata.data.list.length < count) {
                    commit('setTotalDistanceLoaded', true);
                }
            }
            commit('setTotalDistanceRequesting', false);
            return rdata;
        },

        /**
         * 获取排行顶部banner
         */
        async getRankBanner({ state, commit }, { activityId, forceUpdate = false}: { activityId: number, forceUpdate: boolean }) {
            const temp = state.rankBannerMap[activityId];
            if (temp && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/activity/rank_banner', {
                params: {
                    id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('setRankBannerMap', {
                    activityId,
                    list: data,
                });
            }
            return rdata;
        },

        /**
         * 获取坚持天数排行榜
         */
        async getPlatformTotalDaysRank({ state, commit }, { offset, count, forceUpdate = false}: { offset: number; count: number; forceUpdate: boolean }) {
            const wrap = state.platformTotalDaysRank;
            if (wrap.loaded || wrap.requesting) {
                return;
            }
            const list = wrap.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setPlatformTotalDaysRankReq', true);
            const response = await Vue.axios.get('/api/v1/user/total_days_rank', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data.list;
                const user = rdata.data.current;
                commit('concatPlatformTotalDaysRank', list);
                if (list.length < count) {
                    commit('setPlatformTotalDaysRankLoaded', true);
                }
                if (user) {
                    commit('setPlatformTotalDaysRankUser', user);
                }
            }
            commit('setPlatformTotalDaysRankReq', false);
            return rdata;
        },

        /**
         * 获取推荐排行榜
         */
        async getPlatformReferrerRank({ state, commit }, { offset, count, forceUpdate = false}: { offset: number; count: number; forceUpdate: boolean }) {
            const wrap = state.platformReferrerRank;
            if (wrap.loaded || wrap.requesting) {
                return;
            }
            const list = wrap.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setPlatformReferrerRankReq', true);
            const response = await Vue.axios.get('/api/v1/user/referrer_rank', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data.list;
                const user = rdata.data.current;
                commit('concatPlatformReferrerRank', list);
                if (list.length < count) {
                    commit('setPlatformReferrerRankLoaded', true);
                }
                if (user) {
                    commit('setPlatformReferrerRankUser', user);
                }
            }
            commit('setPlatformReferrerRankReq', false);
            return rdata;
        },

        /**
         * 获取坚持届数榜单
         */
        async getPlatformTermRank({ state, commit }, { offset, count, forceUpdate = false}: { offset: number; count: number; forceUpdate: boolean }) {
            const wrap = state.platformTermRank;
            if (wrap.loaded || wrap.requesting) {
                return;
            }
            const list = wrap.list;
            const end = offset + count;
            if (end <= list.length) {
                return;
            }
            if (offset < list.length) {
                offset = list.length;
                count = end - offset;
            }
            commit('setPlatformTermRankReq', true);
            const response = await Vue.axios.get('/api/v1/user/finish_count_rank', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data.list;
                const user = rdata.data.current;
                commit('concatPlatformTermRank', list);
                if (list.length < count) {
                    commit('setPlatformTermRankLoaded', true);
                }
                if (user) {
                    commit('setPlatformTermRankUser', user);
                }
            }
            commit('setPlatformTermRankReq', false);
            return rdata;
        },

        /**
         * 点赞/取消点赞
         */
        async platformThumbUp({ state, commit }, { id, type }) {
            const response = await Vue.axios.post('/api/v1/user/thumb_up', {
                id,
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const item = state.platformRankItem[type + '_' + id];
                if (item) {
                    commit('setPlatformThumbUp', {
                        id: type + '_' + id,
                        hasThumbUp: !item.has_thumb_up,
                    });
                }
            }
            return rdata;
        },
    },
};

export default m;
